import React from "react";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import colors from "../../../assets/theme/colors";
import ChildPhotoUploader from "../ChildPhotoUploader";
import { CustomizableAutocomplete } from "../../UI";
import LogFormShell from "../../UI/LogFormShell";
import SupportGoalsSection from "./SupportGoalsSection";
import CareFocusSelectionFields from "./CareFocusSelectionFields";
import AllergiesFoodSection from "./AllergiesFoodSection";
import { getProfileLensOptions } from "./profileLensOptions";
import ProfileDateOfBirthFields from "./ProfileDateOfBirthFields";

const storyTheme = colors.semantic.profileFlow.careStory;

const fieldSx = {
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
    fontWeight: 800,
    color: storyTheme.primaryDark,
  },
  "& .MuiInputBase-root": {
    minHeight: 54,
    borderRadius: "16px",
    bgcolor: colors.semantic.profileFlow.fieldBg,
    color: storyTheme.text,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: storyTheme.cardBorder,
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: storyTheme.primary,
    borderWidth: "1px",
  },
};

const getTone = (toneKey = "who") => storyTheme.tones[toneKey] || storyTheme.tones.who;

const FieldCard = ({ title, children }) => (
  <Box
    sx={{
      p: { xs: 1.35, sm: 1.6 },
      borderRadius: "22px",
      border: `1px solid ${storyTheme.cardBorder}`,
      bgcolor: storyTheme.cardBg,
    }}
  >
    <Stack spacing={1.15}>
      <Typography
        sx={{
          color: storyTheme.muted,
          fontSize: "0.78rem",
          fontWeight: 900,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>
      {children}
    </Stack>
  </Box>
);

const TagPrompt = ({
  label,
  value,
  onChange,
  options,
  addText,
  helperText,
  getOptionLabel,
  renderTags = null,
}) => (
  <CustomizableAutocomplete
    options={options}
    value={value || []}
    onChange={(event, nextValue) => onChange?.(nextValue)}
    label={label}
    addText={addText}
    helperText={helperText}
    getOptionLabel={getOptionLabel}
    renderTags={renderTags}
    sx={fieldSx}
  />
);

const TextPrompt = ({ label, value, onChange, placeholder = "", helperText = "", minRows = 3 }) => (
  <TextField
    label={label}
    placeholder={placeholder}
    helperText={helperText}
    multiline
    minRows={minRows}
    fullWidth
    value={value || ""}
    onChange={(event) => onChange?.(event.target.value)}
    sx={fieldSx}
  />
);

const CareProfileStoryEditSheet = ({
  open,
  chapter,
  onClose,
  displayName,
  name,
  setName,
  dateOfBirth,
  setDateOfBirth,
  setPhoto,
  photoURL,
  setPhotoURL,
  selectedConditions,
  setSelectedConditions,
  careContext,
  onCareContextChange,
  foodAllergies,
  setFoodAllergies,
  dietaryRestrictions,
  setDietaryRestrictions,
  sensoryIssues,
  setSensoryIssues,
  behavioralTriggers,
  setBehavioralTriggers,
  communicationNeeds,
  setCommunicationNeeds,
  supportProfile,
  setSupportProfile,
  renderMedicationDetails,
  renderDocumentDropZone,
  onDone,
  isSaving = false,
}) => {
  const safeSupportProfile = supportProfile || {};
  const titleName = String(displayName || name || "this person").trim();
  const tone = getTone(chapter?.tone);
  const lensOptions = getProfileLensOptions(selectedConditions);
  const supportOptions = lensOptions.supportProfile;

  const updateSupportField = (key, value) => {
    setSupportProfile?.((current) => ({ ...(current || {}), [key]: value }));
  };

  const renderWho = () => (
    <>
      <FieldCard title="About">
        <ChildPhotoUploader
          setPhoto={setPhoto}
          photoURL={photoURL}
          setPhotoURL={setPhotoURL}
          label="Profile photo"
          compactRow
        />
        <TextField label="Name" fullWidth value={name || ""} onChange={(event) => setName?.(event.target.value)} sx={fieldSx} />
        <ProfileDateOfBirthFields
          dateOfBirth={dateOfBirth}
          onDateOfBirthChange={setDateOfBirth}
          fallbackAgeGroup={careContext?.ageGroup}
          fieldSx={fieldSx}
        />
        <CareFocusSelectionFields
          value={selectedConditions}
          onChange={setSelectedConditions}
          fieldSx={fieldSx}
        />
        <TagPrompt
          label="Tracking focus"
          value={careContext?.trackingFocus}
          onChange={(value) => onCareContextChange?.("trackingFocus", value)}
          options={lensOptions.trackingFocus}
          addText="Add focus"
          getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
        />
        <TagPrompt
          label="Care setting"
          value={careContext?.careSetting}
          onChange={(value) => onCareContextChange?.("careSetting", value)}
          options={lensOptions.careSetting}
          addText="Add setting"
          getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
        />
      </FieldCard>
      <FieldCard title={`Who ${titleName} is`}>
        <TextPrompt
          label={`About ${titleName}`}
          value={safeSupportProfile.about}
          onChange={(value) => updateSupportField("about", value)}
          placeholder="Personality, routines, preferences, and what matters most."
        />
        <TagPrompt
          label="Communication style"
          value={safeSupportProfile.communicationStyle}
          onChange={(value) => updateSupportField("communicationStyle", value)}
          options={supportOptions.communicationStyle}
          addText="Add style"
        />
        <TagPrompt
          label="Communication support"
          value={communicationNeeds}
          onChange={setCommunicationNeeds}
          options={lensOptions.communication}
          addText="Add support"
        />
        <TextPrompt
          label="Support notes"
          value={safeSupportProfile.communicationNotes}
          onChange={(value) => updateSupportField("communicationNotes", value)}
          placeholder="Give processing time, offer choices, use picture options..."
        />
      </FieldCard>
    </>
  );

  const renderHardMoments = () => (
    <>
      <FieldCard title="Hard moments">
        <TagPrompt label="Triggers and hard moments" value={behavioralTriggers} onChange={setBehavioralTriggers} options={lensOptions.triggers} addText="Add trigger" />
        <TagPrompt label="What can be difficult" value={safeSupportProfile.whatCanBeDifficult} onChange={(value) => updateSupportField("whatCanBeDifficult", value)} options={supportOptions.whatCanBeDifficult} addText="Add hard thing" />
        <TagPrompt label="Early warning signs" value={safeSupportProfile.earlyWarningSigns} onChange={(value) => updateSupportField("earlyWarningSigns", value)} options={supportOptions.earlyWarningSigns} addText="Add sign" />
      </FieldCard>
      <FieldCard title="Safety">
        <TagPrompt label="Safety notes" value={safeSupportProfile.safetyConsiderations} onChange={(value) => updateSupportField("safetyConsiderations", value)} options={supportOptions.safetyConsiderations} addText="Add safety note" />
        {renderDocumentDropZone?.("behavioral", "Support documents", "Optional. Upload behavior plans, safety plans, or care guidance.")}
      </FieldCard>
    </>
  );

  const renderWhatHelps = () => (
    <FieldCard title="What helps">
      <TagPrompt label="What helps" value={safeSupportProfile.whatHelps} onChange={(value) => updateSupportField("whatHelps", value)} options={supportOptions.whatHelps} addText="Add support" />
      <TagPrompt label="Things that usually help" value={safeSupportProfile.thingsThatUsuallyHelp} onChange={(value) => updateSupportField("thingsThatUsuallyHelp", value)} options={supportOptions.thingsThatUsuallyHelp} addText="Add help" />
      <TagPrompt label="Things to avoid" value={safeSupportProfile.thingsToAvoid} onChange={(value) => updateSupportField("thingsToAvoid", value)} options={supportOptions.thingsToAvoid} addText="Add avoid" />
      <TextPrompt
        label="Caregiver notes"
        value={safeSupportProfile.communicationNotes}
        onChange={(value) => updateSupportField("communicationNotes", value)}
        placeholder="Use a calm voice, give space, pause demands..."
      />
    </FieldCard>
  );

  const renderSensory = () => (
    <FieldCard title="Sensory world">
      <TagPrompt label="Sensory or comfort needs" value={sensoryIssues} onChange={setSensoryIssues} options={lensOptions.sensory} addText="Add need" />
      <TagPrompt label="Helpful tools" value={safeSupportProfile.thingsThatUsuallyHelp} onChange={(value) => updateSupportField("thingsThatUsuallyHelp", value)} options={lensOptions.sensorySupports} addText="Add tool" />
      <TagPrompt label="Things to avoid" value={safeSupportProfile.thingsToAvoid} onChange={(value) => updateSupportField("thingsToAvoid", value)} options={supportOptions.thingsToAvoid} addText="Add avoid" />
    </FieldCard>
  );

  const renderRhythm = () => (
    <FieldCard title="Daily rhythm">
      <SupportGoalsSection
        supportProfile={safeSupportProfile}
        setSupportProfile={setSupportProfile}
        childDisplayName={titleName}
        sectionGroup="goals"
        showIntro={false}
        supportOptions={supportOptions}
      />
    </FieldCard>
  );

  const renderHealth = () => (
    <>
      <FieldCard title="Health & medications">
        {renderMedicationDetails?.()}
      </FieldCard>
      <AllergiesFoodSection
        childName={titleName}
        foodAllergies={foodAllergies}
        setFoodAllergies={setFoodAllergies}
        dietaryRestrictions={dietaryRestrictions}
        setDietaryRestrictions={setDietaryRestrictions}
        renderDocumentDropZone={renderDocumentDropZone}
      />
      {renderDocumentDropZone?.("medications", "Medication documents", "Optional. Upload medication plans, labels, or prescriber instructions.")}
    </>
  );

  const renderEmergency = () => (
    <FieldCard title="Emergency contacts">
      <TextPrompt
        label="Emergency contacts"
        value={safeSupportProfile.emergencyContacts}
        onChange={(value) => updateSupportField("emergencyContacts", value)}
        placeholder={"Sarah (mom): 555-123-4567\nDr. Patel: 555-222-3333"}
        helperText="One contact per line works best for the story and share view."
        minRows={4}
      />
    </FieldCard>
  );

  const contentByChapter = {
    who: renderWho,
    hardMoments: renderHardMoments,
    whatHelps: renderWhatHelps,
    sensory: renderSensory,
    rhythm: renderRhythm,
    health: renderHealth,
    emergency: renderEmergency,
  };
  const renderContent = contentByChapter[chapter?.id] || renderWho;

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={chapter?.title || "Edit care story"}
      subtitle={chapter?.sourceLabel ? `From ${chapter.sourceLabel} in your care profile` : "Update the profile details that feed this story."}
      compactTitle
      forceDrawer
      mobileBreakpoint="md"
      headerContent={(
        <Chip
          label="Story section"
          sx={{
            width: "fit-content",
            borderRadius: 999,
            bgcolor: tone.chipBg,
            color: tone.chipInk,
            fontWeight: 850,
          }}
        />
      )}
      bodySx={{
        px: { xs: 2, sm: 3 },
        pt: 1.2,
        pb: 2,
        bgcolor: storyTheme.pageBg,
      }}
      surfaceSx={{
        maxWidth: 760,
        mx: "auto",
        height: "min(92dvh, 92vh)",
        maxHeight: "min(92dvh, 92vh)",
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
      }}
      footer={(
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onDone || onClose}
          disabled={isSaving}
          sx={{
            minHeight: 56,
            borderRadius: "18px",
            textTransform: "none",
            fontWeight: 900,
            bgcolor: storyTheme.primary,
            color: colors.semantic.profileFlow.cardBg,
            "&:hover": { bgcolor: storyTheme.primaryDark },
          }}
        >
          {isSaving ? "Saving..." : "Done"}
        </Button>
      )}
    >
      <Stack spacing={1.2}>
        {renderContent()}
      </Stack>
    </LogFormShell>
  );
};

export default CareProfileStoryEditSheet;
